import isEmpty from "lodash/isEmpty";

import type { ICategory } from "app/common/services/categoryService.types";
import type { chosenSites as chosenSitesType } from "app/common/services/chosenSites";
import type { ILegendItem } from "app/components/React/Legends/legendsTypes";
import { CHART_COLORS } from "app/constants/ChartColors";
import { orderedTrafficSources } from "app/Shared/utils";

import { translateSourceText, getPointY, getCategoryDataPoints } from "../ChannelsOverviewBarChartConfig";
import type { DataFormat } from "../ChannelsOverviewBarChartContainerFormat";
import type { IChannelsData, IData, IDomainsData, ISeries, ITrafficSources } from "../types";

type ChosenSitesType = typeof chosenSitesType;

export const processDataToShow = (
    dataToShow: IChannelsData,
    key: string,
    legends: ILegendItem[],
    dataFormat: DataFormat,
    isCompare: boolean,
    chosenSites: ChosenSitesType,
    selectedCategory: ICategory,
    colorMap: Record<string, string> = CHART_COLORS.trafficSourcesColorsBySourceMMX,
) => {
    if (!dataToShow || isEmpty(dataToShow)) {
        return { channels: [], isLoading: false };
    }

    // Build the union of sources present in any domain's data, in priority order.
    // This mirrors the xAxis categories so each domain's data array aligns by index —
    // otherwise, a domain missing a source (e.g. "Display Ads") shifts later values
    // into the wrong category position.
    const sourcesPresentInData = new Set<string>();
    Object.values(dataToShow).forEach((entry: { trafficSources?: ITrafficSources } | undefined) => {
        Object.keys(entry?.trafficSources ?? {}).forEach((s) => sourcesPresentInData.add(s));
    });
    const alignedSources = orderedTrafficSources.filter((source) => sourcesPresentInData.has(source));

    const sitesDataPoints = key.split(",").reduce((acc, domainName) => {
        const domainData = dataToShow[domainName];
        if (domainData && legends.find((l) => l.name === domainName)?.visible) {
            const { trafficSources, totalVisits } = domainData;
            const dataPoints = alignedSources.map((sourceName) => {
                const hasSource = Object.prototype.hasOwnProperty.call(trafficSources, sourceName);
                return {
                    key: translateSourceText(sourceName),
                    y: hasSource ? getPointY(trafficSources[sourceName], totalVisits, dataFormat) : null,
                    color: isCompare ? chosenSites.getSiteColor(domainName) : colorMap[sourceName],
                };
            });
            acc.push({
                name: domainName,
                data: dataPoints,
                id: domainName,
            });
        }
        return acc;
    }, [] as ISeries[]);

    let categoryDataPoints: ISeries[] = [];
    if (dataToShow.category && legends.find((l) => l.name === selectedCategory?.text)?.visible) {
        categoryDataPoints = getCategoryDataPoints(dataToShow, dataFormat, sitesDataPoints.length);
    }

    const channels = [...sitesDataPoints, ...categoryDataPoints];
    return { channels, isLoading: false };
};

const getYDomainpoint = (pointValue: number, totalVisits: number, isPercentage: boolean) => {
    if (isPercentage) {
        return totalVisits !== 0 ? pointValue / totalVisits : 0;
    } else {
        return pointValue;
    }
};

export const processDataDomainToShow = (
    data: IDomainsData,
    rawData,
    isPercentage: boolean,
    trafficSourcesMap: Record<string, { text: string; color: string }>,
): ISeries[] => {
    return Object.values(trafficSourcesMap).reduce((acc, source) => {
        const { text, color } = source;
        if (!data[text]) {
            return acc; // Skip this source if it doesn't exist in the data
        }

        const seriesData: IData[] = Object.keys(data[text]).map((domain) => ({
            key: domain,
            y: getYDomainpoint(data[text][domain], rawData[domain].totalVisits, isPercentage),
        }));

        acc.push({
            data: seriesData,
            id: text,
            name: text,
            color: color,
        });

        return acc;
    }, [] as ISeries[]);
};
